import { useState, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, Check, AlertTriangle, Package, Receipt, Loader2 } from "lucide-react";

const EXPENSE_CATEGORIES = ['Products', 'Rent', 'Utilities', 'Marketing', 'Equipment', 'Insurance', 'Training', 'General', 'Other'];

export default function InvoiceUpload({ open, onClose, onPosted, defaultPostTo = 'inventory' }) {
  const [step, setStep] = useState('upload'); // upload, parsing, review, done
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [invoiceFileId, setInvoiceFileId] = useState(null);
  const [supplierName, setSupplierName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [parseError, setParseError] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setStep('parsing');
    setParseError(null);
    setDebugInfo(null);
    
    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await axios.post(`${API}/invoices/parse`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000
      });
      
      if (res.data.debug) setDebugInfo(res.data.debug);
      
      if (res.data.success || res.data.parsed) {
        const p = res.data.parsed || {};
        setParsed(p);
        setInvoiceFileId(res.data.invoice_file_id);
        setSupplierName(p.supplier_name || '');
        setInvoiceNumber(p.invoice_number || '');
        setInvoiceDate(p.invoice_date || new Date().toISOString().slice(0, 10));
        setLineItems((p.line_items || []).map((item, i) => ({
          ...item,
          _idx: i,
          post_to: item.post_to || defaultPostTo,
          category: item.category || 'Products'
        })));
        if (res.data.parse_error) setParseError(res.data.parse_error);
        setStep('review');
      } else {
        toast.error(res.data.error || 'Failed to parse');
        setStep('upload');
      }
    } catch (err) {
      toast.error('Failed to parse invoice');
      setStep('upload');
    }
  };

  const updateLine = (idx, field, value) => {
    setLineItems(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const handlePost = async () => {
    setPosting(true);
    try {
      const res = await axios.post(`${API}/invoices/post`, {
        invoice_file_id: invoiceFileId,
        supplier_name: supplierName,
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
        line_items: lineItems
      });
      if (res.data.success) {
        setResult(res.data);
        setStep('done');
        toast.success(`Invoice posted: ${res.data.inventory_posted} to inventory, ${res.data.expense_posted} to expenses`);
      } else {
        toast.error(res.data.error || 'Failed to post');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post invoice');
    }
    setPosting(false);
  };

  const reset = () => {
    setStep('upload'); setFile(null); setParsed(null); setLineItems([]);
    setSupplierName(''); setInvoiceNumber(''); setInvoiceDate('');
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const confidenceColor = (c) => {
    if (c === 'high') return 'bg-green-100 text-green-700';
    if (c === 'medium') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-500';
  };

  return (
    <Dialog open={open} onOpenChange={() => { reset(); onClose(); }}>
      <DialogContent className="rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Playfair Display' }} className="flex items-center gap-2">
            <Upload size={20} className="text-primary" />
            {step === 'upload' && 'Upload Invoice'}
            {step === 'parsing' && 'Processing Invoice...'}
            {step === 'review' && 'Review Extracted Data'}
            {step === 'done' && 'Invoice Posted'}
          </DialogTitle>
        </DialogHeader>

        {/* Upload step */}
        {step === 'upload' && (
          <div className="space-y-4">
            <div 
              className="border-2 border-dashed border-border rounded-xl p-10 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              <FileText size={36} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">Click to upload invoice</p>
              <p className="text-xs text-muted-foreground mt-1">PDF, JPG, or PNG</p>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUpload} className="hidden" />
            </div>
          </div>
        )}

        {/* Parsing step */}
        {step === 'parsing' && (
          <div className="py-12 text-center">
            <Loader2 size={40} className="mx-auto text-primary animate-spin mb-4" />
            <p className="text-sm font-medium">Processing {file?.name}...</p>
            <p className="text-xs text-muted-foreground mt-1">AI is reading the invoice and extracting line items</p>
          </div>
        )}

        {/* Review step */}
        {step === 'review' && (
          <div className="space-y-4">
            {/* Parse error notice */}
            {parseError && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-800">
                <AlertTriangle size={14} className="inline mr-1" /> {parseError}
              </div>
            )}
            
            {/* Invoice header */}
            <div className="grid grid-cols-3 gap-3 p-3 bg-muted/30 rounded-xl">
              <div>
                <Label className="text-[10px]">Supplier</Label>
                <Input value={supplierName} onChange={e => setSupplierName(e.target.value)} className="mt-1 rounded-lg h-8 text-xs" />
              </div>
              <div>
                <Label className="text-[10px]">Invoice #</Label>
                <Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} className="mt-1 rounded-lg h-8 text-xs" />
              </div>
              <div>
                <Label className="text-[10px]">Date</Label>
                <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} className="mt-1 rounded-lg h-8 text-xs" />
              </div>
            </div>
            
            {parsed?.total > 0 && (
              <div className="flex gap-4 text-xs">
                {parsed.subtotal > 0 && <span>Subtotal: <strong>£{parsed.subtotal.toFixed(2)}</strong></span>}
                {parsed.vat > 0 && <span>VAT: <strong>£{parsed.vat.toFixed(2)}</strong></span>}
                <span>Total: <strong className="text-primary">£{parsed.total.toFixed(2)}</strong></span>
              </div>
            )}

            {/* Line items */}
            <div>
              <Label className="text-xs font-semibold mb-2 block">{lineItems.length} Line Items</Label>
              <div className="space-y-2">
                {lineItems.map((item, i) => (
                  <div key={i} className="p-3 border rounded-xl bg-card space-y-2">
                    <div className="flex items-center gap-2">
                      <Input value={item.name || ''} onChange={e => updateLine(i, 'name', e.target.value)} className="rounded-lg h-7 text-xs flex-1" placeholder="Product name" />
                      <Input value={item.sku || ''} onChange={e => updateLine(i, 'sku', e.target.value)} className="rounded-lg h-7 text-xs w-24" placeholder="SKU" />
                      <span className="text-[10px] text-muted-foreground">Qty:</span>
                      <Input type="number" value={item.quantity || 1} onChange={e => updateLine(i, 'quantity', parseInt(e.target.value) || 1)} className="rounded-lg h-7 text-xs w-14 text-center" />
                      <span className="text-[10px]">£</span>
                      <Input type="number" step="0.01" value={item.unit_price || 0} onChange={e => updateLine(i, 'unit_price', parseFloat(e.target.value) || 0)} className="rounded-lg h-7 text-xs w-20" />
                      <span className="text-xs font-medium text-primary tabular-nums">= £{((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Match indicator */}
                      {item.match && (
                        <Badge className={`text-[9px] rounded-full ${confidenceColor(item.match_confidence)}`}>
                          {item.match_confidence === 'high' ? '✓' : '~'} Matches: {item.match.name}
                        </Badge>
                      )}
                      {!item.match && <Badge className="text-[9px] rounded-full bg-gray-100 text-gray-500">New product</Badge>}
                      
                      {/* Post to selector */}
                      <div className="ml-auto flex items-center gap-1.5">
                        <span className="text-[10px] text-muted-foreground">Post to:</span>
                        <Select value={item.post_to} onValueChange={v => updateLine(i, 'post_to', v)}>
                          <SelectTrigger className="h-6 rounded-lg text-[10px] w-28 border-0 bg-muted/50">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inventory"><Package size={10} className="inline mr-1" />Inventory</SelectItem>
                            <SelectItem value="expense"><Receipt size={10} className="inline mr-1" />Expense</SelectItem>
                          </SelectContent>
                        </Select>
                        {item.post_to === 'expense' && (
                          <Select value={item.category || 'Products'} onValueChange={v => updateLine(i, 'category', v)}>
                            <SelectTrigger className="h-6 rounded-lg text-[10px] w-24 border-0 bg-muted/50">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Debug info */}
            {debugInfo && (
              <details className="text-[10px] text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground">Parsing debug info</summary>
                <div className="mt-1 p-2 bg-muted/30 rounded-lg font-mono space-y-0.5">
                  <p>File: {debugInfo.filename} ({debugInfo.file_size ? `${(debugInfo.file_size/1024).toFixed(1)}KB` : '?'})</p>
                  <p>Type: {debugInfo.content_type}</p>
                  <p>Parser: {debugInfo.parser}</p>
                  <p>Text extracted: {debugInfo.text_length} chars</p>
                  <p>Confidence: {debugInfo.parse_confidence || 'unknown'}</p>
                  {debugInfo.errors?.length > 0 && <p className="text-red-500">Errors: {debugInfo.errors.join(', ')}</p>}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Done step */}
        {step === 'done' && result && (
          <div className="py-8 text-center space-y-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check size={28} className="text-green-600" />
            </div>
            <h3 className="font-semibold" style={{ fontFamily: 'Playfair Display' }}>Invoice Posted Successfully</h3>
            <div className="flex justify-center gap-6 text-sm">
              {result.inventory_posted > 0 && (
                <div className="text-center">
                  <p className="text-2xl font-semibold text-primary" style={{ fontFamily: 'Playfair Display' }}>{result.inventory_posted}</p>
                  <p className="text-xs text-muted-foreground">to Inventory</p>
                </div>
              )}
              {result.expense_posted > 0 && (
                <div className="text-center">
                  <p className="text-2xl font-semibold text-blue-500" style={{ fontFamily: 'Playfair Display' }}>{result.expense_posted}</p>
                  <p className="text-xs text-muted-foreground">to Expenses</p>
                </div>
              )}
              {result.new_products_created > 0 && (
                <div className="text-center">
                  <p className="text-2xl font-semibold text-green-500" style={{ fontFamily: 'Playfair Display' }}>{result.new_products_created}</p>
                  <p className="text-xs text-muted-foreground">New Products</p>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'review' && (
            <>
              <Button variant="ghost" onClick={() => { reset(); onClose(); }} className="rounded-xl">Cancel</Button>
              <Button onClick={handlePost} disabled={posting} className="rounded-xl">
                {posting ? 'Posting...' : 'Post Invoice'}
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={() => { reset(); onClose(); if (onPosted) onPosted(); }} className="rounded-xl">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
